import React, { useState, useMemo } from 'react';
import { Thermometer, Copy, CheckCircle, DollarSign, Calendar, Filter, CalendarDays, BarChart2, BarChart3, TrendingUp } from 'lucide-react';
import { CalculatedMetrics } from '../types';
import { formatVolume, formatPercentage, calculateMedian } from '../utils/calculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toLocalDateString, parseLocalDate } from '../utils/dateHelpers';

interface MarketThermometerReportProps {
  data: CalculatedMetrics[];
}

interface ThermometerFilters {
  priceRange: [number, number];
  pmVolumeRange: [number, number];
  gapPercentRange: [number, number];
  startDate: Date | null;
  endDate: Date | null;
}

interface ThermometerMetrics {
  pmhBreakPercentage: number;
  closeRedPercentage: number;
  averagePositiveChangePercent: number;
  averageNegativeChangePercent: number;
  averagePmVolume: number;
  medianPmVolume: number;
  averageEodVolume: number;
  medianEodVolume: number;
  averageGapPercent: number;
  medianGapPercent: number;
  averageSpikePercent: number;
  medianSpikePercent: number;
  totalGaps: number;
  bias: 'LONG' | 'SHORT' | 'NEUTRALE';
}

export const MarketThermometerReport: React.FC<MarketThermometerReportProps> = ({ data }) => {
  // Calcola l'intervallo di date disponibili nei dati
  const dateRange = useMemo(() => {
    if (data.length === 0) {
      const today = new Date();
      const oneMonthAgo = new Date(today);
      oneMonthAgo.setMonth(today.getMonth() - 1);
      return { min: oneMonthAgo, max: today };
    }
    
    const dates = data.map(row => row.Date).sort((a, b) => a.getTime() - b.getTime());
    return { min: dates[0], max: dates[dates.length - 1] };
  }, [data]);

  const [filters, setFilters] = useState<ThermometerFilters>({
    priceRange: [0, Infinity],
    pmVolumeRange: [0, Infinity],
    gapPercentRange: [-Infinity, Infinity],
    startDate: dateRange.min,
    endDate: dateRange.max
  });
  const [copySuccess, setCopySuccess] = useState(false);

  // Aggiorna i filtri quando cambiano i dati
  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: dateRange.min,
      endDate: dateRange.max
    }));
  }, [dateRange]);

  // Filtra i dati in base ai filtri selezionati
  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Filtro prezzo apertura
      if (row.Open < filters.priceRange[0] || row.Open > filters.priceRange[1]) {
        return false;
      }

      // Filtro volume pre-market
      const pmVolume = row['Pre Market Volume'] || 0;
      if (pmVolume < filters.pmVolumeRange[0] || pmVolume > filters.pmVolumeRange[1]) {
        return false;
      }

      // Filtro Gap %
      // Il Gap% è salvato come decimale (0.32 = 32%), quindi convertiamo in percentuale per il confronto
      const gapPercent = (Number(row['Gap %']) || 0) * 100;
      if (gapPercent < filters.gapPercentRange[0] || gapPercent > filters.gapPercentRange[1]) {
        return false;
      }

      // Filtro data inizio
      if (filters.startDate && row.Date < filters.startDate) {
        return false;
      }

      // Filtro data fine
      if (filters.endDate && row.Date > filters.endDate) {
        return false;
      }

      return true;
    });
  }, [data, filters]);

  // Calcola le metriche del termometro
  const thermometerMetrics = useMemo((): ThermometerMetrics => {
    if (filteredData.length === 0) {
      return {
        pmhBreakPercentage: 0,
        closeRedPercentage: 0,
        averagePositiveChangePercent: 0,
        averageNegativeChangePercent: 0,
        averagePmVolume: 0,
        medianPmVolume: 0,
        averageEodVolume: 0,
        medianEodVolume: 0,
        averageGapPercent: 0,
        medianGapPercent: 0,
        averageSpikePercent: 0,
        medianSpikePercent: 0,
        totalGaps: 0,
        bias: 'NEUTRALE'
      };
    }

    // Calcola PMH Break %
    const pmhBreaks = filteredData.filter(row => row['HIGH > PMH'] === 'Y').length;
    const pmhBreakPercentage = (pmhBreaks / filteredData.length) * 100;

    // Calcola Chiusure Rosse %
    const redCloses = filteredData.filter(row => row['Red Close'] === 1).length;
    const closeRedPercentage = (redCloses / filteredData.length) * 100;

    // Separa chiusure positive e negative per calcolare le medie
    const positiveCloses = filteredData.filter(row => row['Red Close'] === 0);
    const negativeCloses = filteredData.filter(row => row['Red Close'] === 1);

    const positiveChangeValues = positiveCloses.map(row => Number(row['Change %']) || 0);
    const negativeChangeValues = negativeCloses.map(row => Number(row['Change %']) || 0);

    const averagePositiveChangePercent = positiveChangeValues.length > 0
      ? positiveChangeValues.reduce((sum, val) => sum + val, 0) / positiveChangeValues.length
      : 0;

    const averageNegativeChangePercent = negativeChangeValues.length > 0
      ? negativeChangeValues.reduce((sum, val) => sum + val, 0) / negativeChangeValues.length
      : 0;

    // Calcola medie e mediane volume
    const pmVolumeValues = filteredData.map(row => Number(row['Pre Market Volume']) || 0);
    const eodVolumeValues = filteredData.map(row => Number(row.Volume) || 0);
    
    const averagePmVolume = pmVolumeValues.reduce((sum, val) => sum + val, 0) / pmVolumeValues.length;
    const medianPmVolume = calculateMedian(pmVolumeValues);
    const averageEodVolume = eodVolumeValues.reduce((sum, val) => sum + val, 0) / eodVolumeValues.length;
    const medianEodVolume = calculateMedian(eodVolumeValues);

    // Calcola medie e mediane Gap %
    // I valori sono salvati come decimali (0.32 = 32%), quindi li usiamo direttamente
    const gapPercentValues = filteredData.map(row => Number(row['Gap %']) || 0);
    const averageGapPercent = gapPercentValues.reduce((sum, val) => sum + val, 0) / gapPercentValues.length;
    const medianGapPercent = calculateMedian(gapPercentValues);

    // Calcola medie e mediane Spike %
    const spikePercentValues = filteredData.map(row => Number(row['Spike %']) || 0);
    const averageSpikePercent = spikePercentValues.reduce((sum, val) => sum + val, 0) / spikePercentValues.length;
    const medianSpikePercent = calculateMedian(spikePercentValues);

    // Calcola il bias generale
    const bias = closeRedPercentage < 45 ? 'LONG' : closeRedPercentage > 55 ? 'SHORT' : 'NEUTRALE';

    return {
      pmhBreakPercentage,
      closeRedPercentage,
      averagePositiveChangePercent,
      averageNegativeChangePercent,
      averagePmVolume,
      medianPmVolume,
      averageEodVolume,
      medianEodVolume,
      averageGapPercent,
      medianGapPercent,
      averageSpikePercent,
      medianSpikePercent,
      totalGaps: filteredData.length,
      bias
    };
  }, [filteredData]);

  // Genera il testo del report
  const generateReportText = (): string => {
    const priceRangeText = filters.priceRange[1] === Infinity
      ? `$${filters.priceRange[0]}+`
      : `$${filters.priceRange[0]} - $${filters.priceRange[1]}`;

    const pmVolumeRangeText = filters.pmVolumeRange[1] === Infinity
      ? `${formatVolume(filters.pmVolumeRange[0])}+`
      : `${formatVolume(filters.pmVolumeRange[0])} - ${formatVolume(filters.pmVolumeRange[1])}`;

    const gapPercentRangeText =
      filters.gapPercentRange[0] === -Infinity && filters.gapPercentRange[1] === Infinity
        ? 'Tutti'
        : filters.gapPercentRange[0] === -Infinity
        ? `≤ ${filters.gapPercentRange[1]}%`
        : filters.gapPercentRange[1] === Infinity
        ? `≥ ${filters.gapPercentRange[0]}%`
        : `${filters.gapPercentRange[0]}% - ${filters.gapPercentRange[1]}%`;

    const periodText = filters.startDate && filters.endDate
      ? `${format(filters.startDate, 'dd/MM/yyyy', { locale: it })} - ${format(filters.endDate, 'dd/MM/yyyy', { locale: it })}`
      : 'Periodo completo';

    return `TERMOMETRO DEL MERCATO - ${periodText}
Fascia Prezzo: ${priceRangeText}
Volume Pre-Market: ${pmVolumeRangeText}
Gap %: ${gapPercentRangeText}
Gap Analizzati: ${thermometerMetrics.totalGaps}

METRICHE CHIAVE:
• Break PMH: ${thermometerMetrics.pmhBreakPercentage.toFixed(1)}%
• Chiusure Rosse: ${thermometerMetrics.closeRedPercentage.toFixed(1)}%
• Chiusura Positiva: ${formatPercentage(thermometerMetrics.averagePositiveChangePercent)}
• Chiusura Negativa: ${formatPercentage(thermometerMetrics.averageNegativeChangePercent)}
• Volume Pre-Market Medio: ${formatVolume(thermometerMetrics.averagePmVolume)} (Mediana: ${formatVolume(thermometerMetrics.medianPmVolume)})
• Volume EOD Medio: ${formatVolume(thermometerMetrics.averageEodVolume)} (Mediana: ${formatVolume(thermometerMetrics.medianEodVolume)})
• Gap % Medio: ${formatPercentage(thermometerMetrics.averageGapPercent)} (Mediana: ${formatPercentage(thermometerMetrics.medianGapPercent)})
• Spike % Medio: ${formatPercentage(thermometerMetrics.averageSpikePercent)} (Mediana: ${formatPercentage(thermometerMetrics.medianSpikePercent)})

BIAS GENERALE: ${thermometerMetrics.bias}`;
  };

  // Funzione per copiare il report
  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(generateReportText());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Errore nella copia del report:', error);
    }
  };

  // Funzioni helper per formattare le date per gli input
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return toLocalDateString(date) || '';
  };

  const parseDateFromInput = (dateString: string) => {
    if (!dateString) return null;
    return parseLocalDate(dateString);
    return new Date(dateString);
  };

  // Calcola la posizione del bias meter (0-100, dove 0 = SHORT, 50 = NEUTRALE, 100 = LONG)
  const biasPosition = 100 - thermometerMetrics.closeRedPercentage;

  // Determina il colore del bias meter
  const getBiasMeterColor = () => {
    if (biasPosition > 60) return 'from-green-400 to-green-600';
    if (biasPosition < 40) return 'from-red-400 to-red-600';
    return 'from-yellow-400 to-yellow-600';
  };

  // Determina il colore dell'indicatore per ogni metrica
  const getIndicatorColor = (value: number, type: 'pmh' | 'positive' | 'negative') => {
    switch (type) {
      case 'pmh':
        return value > 60 ? 'from-green-400 to-green-600' : value < 40 ? 'from-red-400 to-red-600' : 'from-blue-400 to-blue-600';
      case 'positive':
        return value > 5 ? 'from-green-400 to-green-600' : 'from-yellow-400 to-yellow-600';
      case 'negative':
        return Math.abs(value) > 5 ? 'from-red-400 to-red-600' : 'from-orange-400 to-orange-600';
      default:
        return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="p-1.5 sm:p-2 bg-orange-400/10 rounded-lg">
            <Thermometer className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">Termometro del Mercato</h3>
            <p className="text-xs sm:text-sm text-gray-400">Report delle condizioni di mercato con filtri personalizzabili</p>
          </div>
        </div>
        <button
          onClick={handleCopyReport}
          className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
            copySuccess 
              ? 'bg-green-600 text-white' 
              : 'bg-orange-600 hover:bg-orange-500 text-white'
          }`}
        >
          {copySuccess ? (
            <>
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Copiato!</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Copia Report</span>
            </>
          )}
        </button>
      </div>

      {/* Filtri */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
        {/* Filtro Prezzo */}
        <div className="space-y-2">
          <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
            <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Prezzo Apertura ($)</span>
          </label>
          <div className="space-y-2">
            <input
              type="number"
              placeholder="Prezzo min"
              value={filters.priceRange[0] === 0 ? '' : filters.priceRange[0]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                priceRange: [Number(e.target.value) || 0, prev.priceRange[1]]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <input
              type="number"
              placeholder="Prezzo max"
              value={filters.priceRange[1] === Infinity ? '' : filters.priceRange[1]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                priceRange: [prev.priceRange[0], Number(e.target.value) || Infinity]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </div>

        {/* Filtro Volume Pre-Market */}
        <div className="space-y-2">
          <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
            <BarChart2 className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Volume Pre-Market</span>
          </label>
          <div className="space-y-2">
            <input
              type="number"
              placeholder="Volume min"
              value={filters.pmVolumeRange[0] === 0 ? '' : filters.pmVolumeRange[0]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                pmVolumeRange: [Number(e.target.value) || 0, prev.pmVolumeRange[1]]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <input
              type="number"
              placeholder="Volume max"
              value={filters.pmVolumeRange[1] === Infinity ? '' : filters.pmVolumeRange[1]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                pmVolumeRange: [prev.pmVolumeRange[0], Number(e.target.value) || Infinity]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </div>

        {/* Filtro Gap % */}
        <div className="space-y-2">
          <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
            <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Gap %</span>
          </label>
          <div className="space-y-2">
            <input
              type="number"
              placeholder="Gap % min"
              step="0.1"
              value={filters.gapPercentRange[0] === -Infinity ? '' : filters.gapPercentRange[0]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                gapPercentRange: [Number(e.target.value) || -Infinity, prev.gapPercentRange[1]]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <input
              type="number"
              placeholder="Gap % max"
              step="0.1"
              value={filters.gapPercentRange[1] === Infinity ? '' : filters.gapPercentRange[1]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                gapPercentRange: [prev.gapPercentRange[0], Number(e.target.value) || Infinity]
              }))}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </div>

        {/* Filtro Periodo - Calendario */}
        <div className="space-y-2">
          <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
            <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Periodo di Riferimento</span>
          </label>
          <div className="space-y-2">
            <input
              type="date"
              value={formatDateForInput(filters.startDate)}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                startDate: parseDateFromInput(e.target.value)
              }))}
              min={formatDateForInput(dateRange.min)}
              max={formatDateForInput(dateRange.max)}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <input
              type="date"
              value={formatDateForInput(filters.endDate)}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                endDate: parseDateFromInput(e.target.value)
              }))}
              min={formatDateForInput(dateRange.min)}
              max={formatDateForInput(dateRange.max)}
              className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </div>

        {/* Statistiche Rapide */}
        <div className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-900/30 rounded-lg border border-gray-600">
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-orange-400">{thermometerMetrics.totalGaps}</p>
            <p className="text-xs text-gray-400">Gap Filtrati</p>
          </div>
        </div>

        {/* Bias Generale */}
        <div className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-900/30 rounded-lg border border-gray-600">
          <div className="text-center">
            <p className={`text-lg sm:text-xl font-bold ${
              thermometerMetrics.bias === 'LONG' ? 'text-green-400' :
              thermometerMetrics.bias === 'SHORT' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {thermometerMetrics.bias}
            </p>
            <p className="text-xs text-gray-400">Bias Generale</p>
          </div>
        </div>
      </div>

      {/* Report Box */}
      <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-3 sm:p-4 mb-4">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="h-4 w-4 text-orange-400" />
          <h4 className="text-sm font-medium text-white">Report Generato</h4>
        </div>
        <pre className="text-xs sm:text-sm text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
          {generateReportText()}
        </pre>
      </div>

      {/* Analisi Relazionale delle Metriche - SPOSTATA SOTTO IL REPORT */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-purple-400/10 rounded-lg">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Analisi Relazionale delle Metriche</h4>
            <p className="text-xs text-gray-400">Visualizzazione dinamica del bias di mercato e performance chiave</p>
          </div>
        </div>
        
        <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
          {/* Bias Meter Centrale */}
          <div className="mb-6">
            <h5 className="text-sm font-medium text-white mb-3 text-center">Bias di Mercato</h5>
            <div className="relative">
              {/* Barra del bias */}
              <div className="w-full h-8 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full relative overflow-hidden">
                {/* Indicatore posizione */}
                <div 
                  className="absolute top-0 h-full w-1 bg-white shadow-lg transition-all duration-500"
                  style={{ left: `${biasPosition}%` }}
                />
                {/* Etichette sulla barra */}
                <div className="absolute inset-0 flex items-center justify-between px-4 text-xs font-bold text-white">
                  <span>SHORT</span>
                  <span>NEUTRALE</span>
                  <span>LONG</span>
                </div>
              </div>
              
              {/* Valore numerico e bias */}
              <div className="text-center mt-3">
                <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full border ${
                  thermometerMetrics.bias === 'LONG' ? 'bg-green-400/10 border-green-400/30 text-green-400' :
                  thermometerMetrics.bias === 'SHORT' ? 'bg-red-400/10 border-red-400/30 text-red-400' :
                  'bg-yellow-400/10 border-yellow-400/30 text-yellow-400'
                }`}>
                  <span className="text-lg font-bold">{thermometerMetrics.bias}</span>
                  <span className="text-sm">({thermometerMetrics.closeRedPercentage.toFixed(1)}% rosse)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Indicatori di Performance */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Break PMH Indicator */}
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-sm font-medium text-white">Break PMH</h6>
                <span className="text-lg font-bold text-blue-400">
                  {thermometerMetrics.pmhBreakPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${getIndicatorColor(thermometerMetrics.pmhBreakPercentage, 'pmh')} transition-all duration-500`}
                  style={{ width: `${thermometerMetrics.pmhBreakPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {thermometerMetrics.pmhBreakPercentage > 60 ? 'Forte momentum rialzista' : 
                 thermometerMetrics.pmhBreakPercentage < 40 ? 'Resistenza al PMH' : 'Momentum moderato'}
              </p>
            </div>

            {/* Chiusura Positiva Indicator */}
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-sm font-medium text-white">Chiusura Positiva</h6>
                <span className="text-lg font-bold text-green-400">
                  {formatPercentage(thermometerMetrics.averagePositiveChangePercent)}
                </span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${getIndicatorColor(thermometerMetrics.averagePositiveChangePercent * 100, 'positive')} transition-all duration-500`}
                  style={{ width: `${Math.min(thermometerMetrics.averagePositiveChangePercent * 100 * 2, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {thermometerMetrics.averagePositiveChangePercent > 0.05 ? 'Forti guadagni medi' : 'Guadagni moderati'}
              </p>
            </div>

            {/* Chiusura Negativa Indicator */}
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-sm font-medium text-white">Chiusura Negativa</h6>
                <span className="text-lg font-bold text-red-400">
                  {formatPercentage(thermometerMetrics.averageNegativeChangePercent)}
                </span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${getIndicatorColor(Math.abs(thermometerMetrics.averageNegativeChangePercent) * 100, 'negative')} transition-all duration-500`}
                  style={{ width: `${Math.min(Math.abs(thermometerMetrics.averageNegativeChangePercent) * 100 * 2, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {Math.abs(thermometerMetrics.averageNegativeChangePercent) > 0.05 ? 'Forti perdite medie' : 'Perdite moderate'}
              </p>
            </div>
          </div>

          {/* Spiegazione della visualizzazione */}
          <div className="mt-4 p-3 bg-blue-400/10 rounded-lg border border-blue-400/20">
            <p className="text-xs text-blue-300">
              💡 <strong>Come interpretare:</strong> Il bias meter centrale mostra la tendenza generale del mercato basata sulle chiusure rosse. 
              Gli indicatori sottostanti mostrano l'intensità delle performance per ogni categoria. 
              Più la barra è piena e colorata, più forte è il segnale in quella direzione.
            </p>
          </div>
        </div>
      </div>

      {/* Metriche Visuali Rapide */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-blue-400">
            {thermometerMetrics.pmhBreakPercentage.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-400">Break PMH</p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-red-400">
            {thermometerMetrics.closeRedPercentage.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-400">Chiusure Rosse</p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-green-400">
            {formatPercentage(thermometerMetrics.averagePositiveChangePercent)}
          </p>
          <p className="text-xs text-gray-400">Chiusura Positiva</p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-yellow-400">
            {formatPercentage(Math.abs(thermometerMetrics.averageNegativeChangePercent))}
          </p>
          <p className="text-xs text-gray-400">Chiusura Negativa</p>
        </div>
      </div>

      {/* Sezione Metriche con Mediane */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-gray-600">
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-base sm:text-lg font-bold text-purple-400">
            {formatVolume(thermometerMetrics.averagePmVolume)}
          </p>
          <p className="text-xs text-gray-400">Volume PM Medio</p>
          <p className="text-xs text-gray-500 mt-1">
            Mediana: {formatVolume(thermometerMetrics.medianPmVolume)}
          </p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-base sm:text-lg font-bold text-cyan-400">
            {formatVolume(thermometerMetrics.averageEodVolume)}
          </p>
          <p className="text-xs text-gray-400">Volume EOD Medio</p>
          <p className="text-xs text-gray-500 mt-1">
            Mediana: {formatVolume(thermometerMetrics.medianEodVolume)}
          </p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-base sm:text-lg font-bold text-indigo-400">
            {formatPercentage(thermometerMetrics.averageGapPercent)}
          </p>
          <p className="text-xs text-gray-400">Gap % Medio</p>
          <p className="text-xs text-gray-500 mt-1">
            Mediana: {formatPercentage(thermometerMetrics.medianGapPercent)}
          </p>
        </div>
        <div className="bg-gray-900/30 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-base sm:text-lg font-bold text-emerald-400">
            {formatPercentage(thermometerMetrics.averageSpikePercent)}
          </p>
          <p className="text-xs text-gray-400">Spike % Medio</p>
          <p className="text-xs text-gray-500 mt-1">
            Mediana: {formatPercentage(thermometerMetrics.medianSpikePercent)}
          </p>
        </div>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        💡 Configura i filtri per prezzo, volume e periodo, poi osserva come cambiano le metriche nel grafico. Clicca "Copia Report" per copiare il testo negli appunti
      </div>
    </div>
  );
};